import PageHeader from "../components/shell/PageHeader";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiAgentStatusForChallenge } from "../api/api";
import {
  Activity,
  AlertTriangle,
  Banknote,
  BarChart3,
  CalendarDays,
  ChevronRight,
  Clock3,
  Database,
  DollarSign,
  Gauge,
  Loader2,
  Percent,
  Plus,
  RefreshCw,
  Rocket,
  Sparkles,
  Target,
  Trophy,
  TrendingUp,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";

const CHALLENGE_SYNC_ENDPOINT = "/api/challenges";
const LEGACY_CHALLENGE_PREFIX = "fullmetrix:challenges:";

const AUTH_TOKEN_KEYS = [
  "token",
  "access_token",
  "accessToken",
  "authToken",
  "AUTH_TOKEN",
  "jwt",
  "jwt_token",
  "jwtToken",
  "fm_token",
  "fullmetrix_token",
  "fullmetrix:token",
  "fullmetrix:auth:token",
];

const OBJECTIVE_PRESETS = [
  { label: "+10%", value: 10 },
  { label: "+25%", value: 25 },
  { label: "+50%", value: 50 },
  { label: "+100%", value: 100 },
];

const cn = (...classes) => classes.filter(Boolean).join(" ");

function ChallengePageStyles() {
  return (
    <style>{`
      .challenge-page {
        font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        text-rendering: geometricPrecision;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      .challenge-glass {
        background: rgba(255,255,255,.82);
        border: 1px solid rgba(226,232,240,.9);
        box-shadow: 0 18px 44px rgba(15,23,42,.055), inset 0 1px 0 rgba(255,255,255,.75);
        backdrop-filter: blur(18px) saturate(145%);
        -webkit-backdrop-filter: blur(18px) saturate(145%);
      }

      .dark .challenge-glass {
        background: rgba(10,14,23,.74);
        border-color: rgba(255,255,255,.09);
        box-shadow: 0 18px 46px rgba(0,0,0,.32), inset 0 1px 0 rgba(255,255,255,.045);
      }

      .challenge-card-optimized,
      .challenge-contained,
      .challenge-modal-card {
        contain: layout paint;
      }

      .challenge-modal-backdrop {
        left: var(--sidebar-current-width, 0px);
        background: rgba(15, 23, 42, .46);
        backdrop-filter: blur(10px) saturate(125%);
        -webkit-backdrop-filter: blur(10px) saturate(125%);
        overscroll-behavior: contain;
        transition: left 180ms ease, background-color 180ms ease;
      }

      .dark .challenge-modal-backdrop { background: rgba(2, 6, 23, .68); }
      @media (max-width: 899px) { .challenge-modal-backdrop { left: 0; } }

      .challenge-modal-card {
        animation: challengeModalIn 160ms ease-out both;
        will-change: transform, opacity;
        transform: translateZ(0);
      }

      @keyframes challengeModalIn {
        from { opacity: 0; transform: translateY(10px) scale(.985); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }

      .challenge-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(148,163,184,.65) transparent; }
      .challenge-scrollbar::-webkit-scrollbar { width: 7px; height: 7px; }
      .challenge-scrollbar::-webkit-scrollbar-track { background: transparent; }
      .challenge-scrollbar::-webkit-scrollbar-thumb { background: rgba(148,163,184,.65); border-radius: 999px; }

      @media (prefers-reduced-motion: reduce) {
        .challenge-modal-card { animation: none !important; }
        .challenge-modal-backdrop { transition: none !important; }
        .challenge-page * { transition-duration: .01ms !important; animation-duration: .01ms !important; }
      }
    `}</style>
  );
}

const moneyFormatter0 = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });
const moneyFormatter2 = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });
const percentFormatter0 = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });
const percentFormatter2 = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });
const dateFormatter = new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
const dateTimeFormatter = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatMoney(value) {
  const amount = Number(value || 0);
  return (amount % 1 === 0 ? moneyFormatter0 : moneyFormatter2).format(amount);
}

function formatPercent(value) {
  const amount = Number(value || 0);
  return (amount % 1 === 0 ? percentFormatter0 : percentFormatter2).format(amount);
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : dateFormatter.format(d);
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : dateTimeFormatter.format(d);
}

function addDays(date, days) {
  const base = date ? new Date(date) : new Date();
  base.setDate(base.getDate() + Number(days || 0));
  return base.toISOString();
}

function clamp(value, min, max) {
  return Math.min(Math.max(Number(value || 0), min), max);
}

function parseJsonSafe(value, fallback = null) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function safeStorageGet(key) {
  try {
    if (typeof window === "undefined") return "";
    return window.localStorage?.getItem(key) || window.sessionStorage?.getItem(key) || "";
  } catch {
    return "";
  }
}

function readAuthToken() {
  for (const key of AUTH_TOKEN_KEYS) {
    const value = safeStorageGet(key).trim();
    if (value && value.split(".").length >= 2) return value;
  }
  return "";
}

function removeLegacyChallengeCache() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const toRemove = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(LEGACY_CHALLENGE_PREFIX)) toRemove.push(key);
    }
    toRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch {}
}

function getApiBaseUrl() {
  try {
    const fromEnv = typeof process !== "undefined" ? process.env?.REACT_APP_API_URL : "";
    return String(fromEnv || "").replace(/\/$/, "");
  } catch {
    return "";
  }
}

async function readJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};
  return parseJsonSafe(text, { raw: text });
}

async function challengeApiRequest(path = "", options = {}) {
  const token = readAuthToken();
  const url = `${getApiBaseUrl()}${CHALLENGE_SYNC_ENDPOINT}${path}`;
  const hasBody = options.body !== undefined && options.body !== null;

  const buildHeaders = (withToken) => ({
    Accept: "application/json",
    ...(hasBody ? { "Content-Type": "application/json" } : {}),
    ...(withToken && token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  });

  const doFetch = (withToken) =>
    fetch(url, {
      credentials: "include",
      ...options,
      headers: buildHeaders(withToken),
    });

  let response = await doFetch(Boolean(token));

  // Important : si une ancienne clé token est restée dans le navigateur,
  // le cookie de session peut être correct mais le Bearer obsolète provoque un 401.
  // Dans ce cas on réessaie sans Authorization.
  if ((response.status === 401 || response.status === 403) && token) {
    response = await doFetch(false);
  }

  const data = await readJsonResponse(response);

  if (!response.ok || data?.error) {
    const error = new Error(data?.error || data?.message || `challenge_api_${response.status}`);
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  return data;
}

function extractChallengeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.challenges)) return payload.challenges;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function extractChallenge(payload, fallback) {
  if (payload?.challenge && typeof payload.challenge === "object") return payload.challenge;
  if (payload?.item && typeof payload.item === "object") return payload.item;
  if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) return payload.data;
  if (payload && typeof payload === "object" && !Array.isArray(payload)) return payload;
  return fallback;
}

function normalizeChallenge(challenge = {}) {
  const startCapital = Number(challenge.startCapital ?? challenge.start_capital ?? 0);
  const targetCapital = Number(challenge.targetCapital ?? challenge.target_capital ?? challenge.targetAmount ?? challenge.target_amount ?? startCapital);
  const currentCapital = Number(challenge.currentCapital ?? challenge.current_capital ?? challenge.equity ?? challenge.balance ?? startCapital);
  const targetProfit = Math.max(targetCapital - startCapital, 0);
  const targetPercent = startCapital > 0 ? (targetProfit / startCapital) * 100 : 0;
  const createdAt = challenge.createdAt || challenge.created_at || new Date().toISOString();
  const startDate = challenge.startDate || challenge.start_at || createdAt;
  const days = Number(challenge.days || challenge.duration_days || 30);

  return {
    id: challenge.id || challenge.uuid || `challenge_${Date.now()}_${Math.random().toString(16).slice(2)}`,
    name: challenge.name || challenge.title || "Challenge automatique",
    objectiveMode: challenge.objectiveMode || challenge.objective_mode || "percent",
    targetPercent: Number(challenge.targetPercent ?? challenge.target_percent ?? targetPercent),
    targetAmount: Number(challenge.targetAmount ?? challenge.target_amount ?? targetCapital),
    targetProfit: Number(challenge.targetProfit ?? challenge.target_profit ?? targetProfit),
    startCapital,
    currentCapital,
    targetCapital,
    days,
    startDate,
    endDate: challenge.endDate || challenge.end_at || addDays(startDate, days),
    status: challenge.status || "active",
    source: challenge.source || "agent",
    entries: Array.isArray(challenge.entries)
      ? challenge.entries.map((entry) => ({
          day: Number(entry.day ?? entry.day_index ?? 0),
          capital: Number(entry.capital ?? entry.equity ?? entry.balance ?? 0),
          date: entry.date || entry.entry_date || entry.created_at || new Date().toISOString(),
          source: entry.source || "agent",
        }))
      : [],
    createdAt,
    updatedAt: challenge.updatedAt || challenge.updated_at || null,
  };
}

function serializeChallengeForApi(challenge) {
  return {
    id: challenge.id,
    name: challenge.name,
    objective_mode: challenge.objectiveMode,
    objectiveMode: challenge.objectiveMode,
    target_percent: challenge.targetPercent,
    targetPercent: challenge.targetPercent,
    target_amount: challenge.targetAmount,
    targetAmount: challenge.targetAmount,
    target_profit: challenge.targetProfit,
    targetProfit: challenge.targetProfit,
    start_capital: challenge.startCapital,
    startCapital: challenge.startCapital,
    current_capital: challenge.currentCapital,
    currentCapital: challenge.currentCapital,
    target_capital: challenge.targetCapital,
    targetCapital: challenge.targetCapital,
    days: challenge.days,
    start_at: challenge.startDate,
    startDate: challenge.startDate,
    end_at: challenge.endDate,
    endDate: challenge.endDate,
    status: challenge.status,
    source: challenge.source || "agent",
    entries: challenge.entries || [],
    created_at: challenge.createdAt,
    createdAt: challenge.createdAt,
    updated_at: challenge.updatedAt,
    updatedAt: challenge.updatedAt,
  };
}

async function fetchRemoteChallenges() {
  const payload = await challengeApiRequest("");
  return extractChallengeList(payload).map(normalizeChallenge);
}

async function createRemoteChallenge(challenge) {
  const payload = await challengeApiRequest("", {
    method: "POST",
    body: JSON.stringify(serializeChallengeForApi(challenge)),
  });
  return normalizeChallenge(extractChallenge(payload, challenge));
}

async function updateRemoteChallenge(challenge) {
  const id = encodeURIComponent(String(challenge?.id || ""));
  if (!id) return null;

  const payload = await challengeApiRequest(`/${id}`, {
    method: "PATCH",
    body: JSON.stringify(serializeChallengeForApi(challenge)),
  });
  return normalizeChallenge(extractChallenge(payload, challenge));
}

function getElapsedDays(challenge) {
  const start = new Date(challenge.startDate).getTime();
  const diff = Math.floor((Date.now() - start) / 86400000);
  return clamp(diff, 0, Number(challenge.days || 1));
}

function getProgress(challenge) {
  const start = Number(challenge.startCapital || 0);
  const target = Number(challenge.targetCapital || 0);
  const current = Number(challenge.currentCapital || start);
  if (target <= start) return 0;
  return clamp(((current - start) / (target - start)) * 100, 0, 100);
}

function getProfitPercent(challenge) {
  const start = Number(challenge.startCapital || 0);
  const current = Number(challenge.currentCapital || start);
  if (start <= 0) return 0;
  return ((current - start) / start) * 100;
}

function getExpectedCapitalAtDay(challenge, day) {
  const days = Math.max(Number(challenge.days || 1), 1);
  const start = Number(challenge.startCapital || 0);
  const target = Number(challenge.targetCapital || start);
  const safeDay = clamp(day, 0, days);
  return start + ((target - start) / days) * safeDay;
}

function buildTrajectory(challenge) {
  const days = Math.max(Number(challenge.days || 1), 1);
  const start = Number(challenge.startCapital || 0);
  const target = Number(challenge.targetCapital || start);
  const dailyTarget = (target - start) / days;
  const entriesByDay = new Map((challenge.entries || []).map((item) => [Number(item.day), item]));

  return Array.from({ length: days + 1 }, (_, index) => {
    const expected = start + dailyTarget * index;
    const entry = entriesByDay.get(index);
    const real = entry ? Number(entry.capital) : null;
    return {
      day: index,
      date: addDays(challenge.startDate, index),
      expected,
      real,
      gap: real !== null ? real - expected : null,
    };
  });
}

function getChallengeStatus(challenge) {
  const progress = getProgress(challenge);
  const elapsed = getElapsedDays(challenge);

  if (challenge.status === "cancelled") return "cancelled";
  if (progress >= 100) return "success";
  if (elapsed >= Number(challenge.days || 1)) return "failed";

  const expectedToday = getExpectedCapitalAtDay(challenge, elapsed);
  const current = Number(challenge.currentCapital || challenge.startCapital || 0);
  return current < expectedToday ? "late" : "active";
}

function getLatestEntries(challenges, limit = 6) {
  return challenges
    .flatMap((challenge) =>
      (challenge.entries || [])
        .filter((entry) => Number(entry.day) > 0)
        .map((entry) => ({ ...entry, challengeId: challenge.id, challengeName: challenge.name })),
    )
    .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime())
    .slice(0, limit);
}

function getChallengeStats(challenges) {
  const total = challenges.length;
  const statuses = challenges.map((challenge) => getChallengeStatus(challenge));
  const active = statuses.filter((status) => status === "active" || status === "late").length;
  const success = statuses.filter((status) => status === "success").length;
  const failed = statuses.filter((status) => status === "failed").length;
  const late = statuses.filter((status) => status === "late").length;
  const profit = challenges.reduce((sum, challenge) => sum + (Number(challenge.currentCapital || 0) - Number(challenge.startCapital || 0)), 0);
  const targetProfit = challenges.reduce((sum, challenge) => sum + Math.max(Number(challenge.targetCapital || 0) - Number(challenge.startCapital || 0), 0), 0);
  const remaining = challenges.reduce((sum, challenge) => sum + Math.max(Number(challenge.targetCapital || 0) - Number(challenge.currentCapital || 0), 0), 0);
  const avgProgress = total ? Math.round(challenges.reduce((sum, challenge) => sum + getProgress(challenge), 0) / total) : 0;
  const bestChallenge = challenges.reduce((best, challenge) => (!best || getProgress(challenge) > getProgress(best) ? challenge : best), null);

  return {
    total,
    active,
    success,
    failed,
    late,
    successRate: total ? Math.round((success / total) * 100) : 0,
    avgProgress,
    profit,
    targetProfit,
    remaining,
    bestChallenge,
    latestEntries: getLatestEntries(challenges),
  };
}

function useAgentAccount({ enabled = true } = {}) {
  const [state, setState] = useState({
    loading: true,
    connected: false,
    token: "",
    accountLogin: "",
    broker: "",
    balance: 0,
    equity: 0,
    currency: "USD",
    lastSync: null,
    error: null,
  });

  const inFlightRef = useRef(false);
  const lastSnapshotRef = useRef("");

  useEffect(() => {
    let mounted = true;
    let interval = null;

    const publish = (nextState) => {
      const snapshot = JSON.stringify(nextState);
      if (snapshot === lastSnapshotRef.current) return;
      lastSnapshotRef.current = snapshot;
      setState(nextState);
    };

    async function loadAgentStatus() {
      if (!enabled || inFlightRef.current) return;

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        publish({ loading: false, connected: false, token: "", accountLogin: "", broker: "", balance: 0, equity: 0, currency: "USD", lastSync: null, error: "offline" });
        return;
      }

      inFlightRef.current = true;
      try {
        const data = await apiAgentStatusForChallenge();
        if (!mounted) return;
        publish({
          loading: false,
          connected: Boolean(data?.connected),
          token: data?.token || "",
          accountLogin: data?.accountLogin || data?.login || data?.account_login || "",
          broker: data?.broker || data?.server || data?.broker_server || "",
          balance: Number(data?.balance || 0),
          equity: Number(data?.equity || data?.balance || 0),
          currency: data?.currency || "USD",
          lastSync: data?.lastSync || data?.last_heartbeat || data?.last_sync || null,
          error: data?.error || null,
        });
      } catch (error) {
        if (!mounted) return;
        publish({ loading: false, connected: false, token: "", accountLogin: "", broker: "", balance: 0, equity: 0, currency: "USD", lastSync: null, error: error?.message || "agent_status_failed" });
      } finally {
        inFlightRef.current = false;
      }
    }

    const tick = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      loadAgentStatus();
    };

    tick();
    interval = window.setInterval(tick, 15000);

    return () => {
      mounted = false;
      if (interval) window.clearInterval(interval);
    };
  }, [enabled]);

  return state;
}

function PrimaryButton({ children, onClick, className = "", type = "button", disabled = false }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex h-10 items-center justify-center gap-2 rounded-[14px] px-5 text-sm font-extrabold transition duration-200",
        disabled
          ? "cursor-not-allowed bg-slate-200 text-slate-400 dark:bg-white/10 dark:text-slate-500"
          : "bg-gradient-to-r from-violet-600 via-fuchsia-600 to-violet-500 text-white   ",
        className,
      )}
    >
      {children}
    </button>
  );
}

function DarkButton({ children, onClick, className = "", type = "button", disabled = false }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex h-10 items-center justify-center gap-2 rounded-[14px] border px-5 text-sm font-extrabold transition duration-200",
        disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-white/10 dark:bg-white/10 dark:text-slate-500"
          : "border-slate-200 bg-slate-950 text-white  hover:bg-violet-700 dark:border-white/10 dark:bg-white dark:text-slate-950 dark:hover:bg-violet-100",
        className,
      )}
    >
      {children}
    </button>
  );
}

function ChallengeHeader({ agent, onCreate, onOpenAgent, onRefresh, refreshing }) {
  const { t } = useTranslation();
  const ready = agent.connected && Number(agent.balance || 0) > 0;

  return (
    <section className="challenge-glass relative overflow-hidden rounded-2xl p-4">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(124,58,237,0.10),transparent_34%),radial-gradient(circle_at_top_right,rgba(14,165,233,0.09),transparent_32%)]" />

      <div className="relative flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        {/* En-tête commun à toutes les pages (PageHeader). */}
        <PageHeader
          icon={Trophy}
          title={t("challenge.header.title")}
          subtitle={t("challenge.header.subtitle")}
        />

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center lg:shrink-0">
          

          {ready ? (
            <PrimaryButton onClick={onCreate}>
              <Plus className="h-4 w-4" />
              {t("challenge.header.create")}
            </PrimaryButton>
          ) : (
            <DarkButton onClick={onOpenAgent}>
              <Wifi className="h-4 w-4" />
              {t("challenge.common.openAgent")}
            </DarkButton>
          )}
        </div>
      </div>
    </section>
  );
}

function InfoBox({ label, value }) {
  const displayValue = value ?? "—";
  return (
    <div className="flex min-h-[74px] min-w-0 flex-col justify-center rounded-2xl border border-white/60 bg-white/75 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-white/[0.04]">
      <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">{label}</p>
      <p title={String(displayValue)} className="mt-1.5 max-w-full break-words text-[14px] font-black leading-snug tracking-[-0.01em] text-slate-950 dark:text-white">
        {displayValue}
      </p>
    </div>
  );
}

function AgentSummaryCard({ agent }) {
  const { t } = useTranslation();
  const ready = agent.connected && Number(agent.balance || 0) > 0;

  return (
    <section className="challenge-glass rounded-2xl p-4 sm:p-5">
      <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-center 2xl:justify-between">
        <div className="flex min-w-0 items-start gap-3.5">
          <div className={cn("grid h-11 w-11 shrink-0 place-items-center rounded-2xl", ready ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-200" : "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300")}>
            {agent.loading ? <Loader2 className="h-5 w-5 animate-spin" /> : ready ? <Wifi className="h-5 w-5" /> : <WifiOff className="h-5 w-5" />}
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[15px] font-black tracking-[-0.015em] text-slate-950 dark:text-white">{t("challenge.agent.title")}</h2>
              <span className={cn("inline-flex h-6 items-center rounded-full px-2.5 text-[11px] font-black", ready ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200" : "bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-200")}>
                {ready ? t("challenge.common.synced") : t("challenge.agent.required")}
              </span>
            </div>
            <p className="mt-1.5 max-w-3xl text-[13px] font-medium leading-5 text-slate-600 dark:text-slate-300">
              {t("challenge.agent.description")}
            </p>
          </div>
        </div>

        <div className="grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 2xl:w-[760px]">
          <InfoBox label={t("challenge.agent.broker")} value={agent.broker || t("challenge.agent.pending")} />
          <InfoBox label={t("challenge.agent.account")} value={agent.accountLogin || "—"} />
          <InfoBox label={t("challenge.agent.capital")} value={ready ? `${formatMoney(agent.balance)}$` : "—"} />
          <InfoBox label={t("challenge.agent.lastSync")} value={formatDateTime(agent.lastSync)} />
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon: Icon, label, value, tone = "violet" }) {
  const tones = {
    violet: "bg-violet-50 text-violet-700 dark:bg-violet-400/10 dark:text-violet-200",
    emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200",
    rose: "bg-rose-50 text-rose-700 dark:bg-rose-400/10 dark:text-rose-200",
    sky: "bg-sky-50 text-sky-700 dark:bg-sky-400/10 dark:text-sky-200",
    amber: "bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-200",
  };

  return (
    <div className="challenge-glass rounded-2xl p-4">
      <div className="flex items-center gap-3">
        <div className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", tones[tone])}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-[10px] font-black uppercase tracking-[0.13em] text-slate-500 dark:text-slate-400">{label}</p>
          <p className="mt-1 text-[24px] font-black leading-none tracking-[-0.04em] text-slate-950 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

function MiniBox({ label, value, active }) {
  return (
    <div className={cn("rounded-xl p-2.5", active ? "bg-violet-50 dark:bg-violet-400/10" : "bg-slate-50 dark:bg-white/[0.04]")}>
      <p className={cn("text-[10px] font-black uppercase tracking-[0.10em]", active ? "text-violet-600 dark:text-violet-300" : "text-slate-500 dark:text-slate-400")}>{label}</p>
      <p className={cn("mt-1 text-[13px] font-black", active ? "text-violet-700 dark:text-violet-200" : "text-slate-950 dark:text-white")}>{value}</p>
    </div>
  );
}

function ChallengeCard({ challenge, onOpen }) {
  const { t } = useTranslation();
  const progress = getProgress(challenge);
  const profitPercent = getProfitPercent(challenge);
  const status = getChallengeStatus(challenge);
  const elapsed = getElapsedDays(challenge);

  const statusConfig = {
    active: { label: t("challenge.status.active"), className: "bg-sky-50 text-sky-700 dark:bg-sky-400/10 dark:text-sky-200" },
    late: { label: t("challenge.status.late"), className: "bg-rose-50 text-rose-700 dark:bg-rose-400/10 dark:text-rose-200" },
    success: { label: t("challenge.status.success"), className: "bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200" },
    failed: { label: t("challenge.status.failed"), className: "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300" },
    cancelled: { label: t("challenge.status.cancelled"), className: "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300" },
  };

  const modeLabel = challenge.objectiveMode === "amount" ? t("challenge.card.targetAmount", { amount: formatMoney(challenge.targetCapital) }) : t("challenge.card.targetPercent", { percent: formatPercent(challenge.targetPercent) });

  return (
    <button
      type="button"
      onClick={() => onOpen(challenge)}
      className="challenge-glass challenge-card-optimized group rounded-2xl p-4 text-left transition  hover:border-violet-300  dark:hover:border-violet-400/40"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-500 text-white ">
            {challenge.objectiveMode === "amount" ? <Banknote className="h-4 w-4" /> : <Percent className="h-4 w-4" />}
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-black tracking-[-0.02em] text-slate-950 dark:text-white">{challenge.name}</h3>
            <p className="mt-1 flex flex-wrap items-center gap-1 text-xs font-medium text-slate-500 dark:text-slate-400">
              <CalendarDays className="h-3.5 w-3.5" />
              {formatDate(challenge.startDate)} → {formatDate(challenge.endDate)}
            </p>
          </div>
        </div>

        <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black", statusConfig[status]?.className)}>
          {statusConfig[status]?.label || "—"}
        </span>
      </div>

      <div className="mt-4 rounded-xl bg-slate-50 px-3 py-2 text-[12px] font-black text-slate-700 dark:bg-white/[0.04] dark:text-slate-200">{modeLabel}</div>

      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <MiniBox label={t("challenge.common.start")} value={`${formatMoney(challenge.startCapital)}$`} />
        <MiniBox label={t("challenge.common.objective")} value={`${formatMoney(challenge.targetCapital)}$`} active />
        <MiniBox label={t("challenge.common.current")} value={`${formatMoney(challenge.currentCapital || challenge.startCapital)}$`} />
        <MiniBox label={t("challenge.card.gain")} value={`${profitPercent >= 0 ? "+" : ""}${formatPercent(profitPercent)}%`} />
      </div>

      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between text-[11px] font-black text-slate-500 dark:text-slate-400">
          <span>{t("challenge.common.progress")}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
          <div className="h-full rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{t("challenge.card.dayCount", { elapsed, days: challenge.days })}</span>
        <span className="inline-flex items-center gap-1 text-xs font-black text-violet-600 dark:text-violet-300">
          {t("challenge.card.details")}
          <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </span>
      </div>
    </button>
  );
}

function OverviewStats({ stats }) {
  const { t } = useTranslation();
  return (
    <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <StatCard icon={Activity} label={t("challenge.overview.active")} value={`${stats.active} / ${stats.total}`} tone="violet" />
      <StatCard icon={Trophy} label={t("challenge.overview.success")} value={stats.success} tone="emerald" />
      <StatCard icon={AlertTriangle} label={t("challenge.overview.late")} value={stats.late} tone="rose" />
      <StatCard icon={Gauge} label={t("challenge.overview.avgProgress")} value={`${stats.avgProgress}%`} tone="sky" />
      <StatCard icon={DollarSign} label={t("challenge.overview.totalProfit")} value={`${formatMoney(stats.profit)}$`} tone="amber" />
    </section>
  );
}

function PerformanceSummary({ stats }) {
  const { t } = useTranslation();
  return (
    <section className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
      <div className="challenge-glass rounded-2xl p-4">
        <div className="flex items-center gap-2 text-slate-950 dark:text-white">
          <BarChart3 className="h-[18px] w-[18px] text-violet-500" />
          <h2 className="text-sm font-black tracking-[-0.01em]">{t("challenge.performance.title")}</h2>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <MiniBox label={t("challenge.performance.totalObjective")} value={`${formatMoney(stats.targetProfit)}$`} />
          <MiniBox label={t("challenge.performance.remaining")} value={`${formatMoney(stats.remaining)}$`} active />
          <MiniBox label={t("challenge.performance.successRate")} value={`${stats.successRate}%`} />
        </div>

        <div className="mt-4 rounded-2xl bg-slate-50 p-4 dark:bg-white/[0.04]">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{t("challenge.performance.best")}</p>
          {stats.bestChallenge ? (
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-black text-slate-950 dark:text-white">{stats.bestChallenge.name}</p>
                <p className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">{t("challenge.performance.bestProgress", { value: Math.round(getProgress(stats.bestChallenge)) })}</p>
              </div>
              <TrendingUp className="h-5 w-5 shrink-0 text-emerald-500" />
            </div>
          ) : (
            <p className="mt-2 text-sm font-medium text-slate-500 dark:text-slate-400">{t("challenge.performance.noChallenge")}</p>
          )}
        </div>
      </div>

      <div className="challenge-glass overflow-hidden rounded-2xl">
        <div className="border-b border-slate-200/80 px-4 py-3 dark:border-white/10">
          <div className="flex items-center gap-2">
            <Clock3 className="h-4 w-4 text-violet-500" />
            <h3 className="text-base font-black text-slate-950 dark:text-white">{t("challenge.performance.latestSyncs")}</h3>
          </div>
        </div>

        {stats.latestEntries.length === 0 ? (
          <div className="px-4 py-8 text-center text-[13px] font-medium text-slate-500 dark:text-slate-400">{t("challenge.performance.noSync")}</div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-white/10">
            {stats.latestEntries.map((entry, index) => (
              <div key={`${entry.challengeId}-${entry.day}-${entry.date}-${index}`} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-black text-slate-950 dark:text-white">{entry.challengeName}</p>
                  <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">{t("challenge.performance.entryMeta", { day: entry.day, datetime: formatDateTime(entry.date) })}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-black text-slate-950 dark:text-white">{formatMoney(entry.capital)}$</p>
                  <p className="text-xs font-bold text-violet-600 dark:text-violet-300">{t("challenge.common.agent")}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

const MiniChart = memo(function MiniChart({ trajectory }) {
  const { t } = useTranslation();
  const chartData = useMemo(() => {
    const safeTrajectory = Array.isArray(trajectory) ? trajectory : [];
    const points = safeTrajectory.filter((item) => item.real !== null);

    if (points.length <= 1) return { points, expectedPath: "", realPath: "" };

    const allValues = safeTrajectory.flatMap((item) => (item.real !== null ? [item.expected, item.real] : [item.expected]));
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const range = Math.max(max - min, 1);
    const divisor = Math.max(safeTrajectory.length - 1, 1);

    const expectedPath = safeTrajectory
      .map((item, index) => {
        const x = (index / divisor) * 100;
        const y = 100 - ((item.expected - min) / range) * 76 - 12;
        return `${x},${y}`;
      })
      .join(" ");

    const realPath = points
      .map((item) => {
        const x = (item.day / divisor) * 100;
        const y = 100 - ((item.real - min) / range) * 76 - 12;
        return `${x},${y}`;
      })
      .join(" ");

    return { points, expectedPath, realPath };
  }, [trajectory]);

  if (chartData.points.length <= 1) {
    return (
      <div className="grid min-h-[132px] place-items-center rounded-2xl border border-slate-200 bg-white/80 px-4 text-center text-[13px] font-medium text-slate-500 dark:border-white/10 dark:bg-slate-950/60 dark:text-slate-400">
        {t("challenge.chart.empty")}
      </div>
    );
  }

  return (
    <div className="challenge-contained rounded-2xl border border-slate-200 bg-white/85 p-4 dark:border-white/10 dark:bg-slate-950/60">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-black text-slate-950 dark:text-white">{t("challenge.chart.title")}</h3>
          <p className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">{t("challenge.chart.subtitle")}</p>
        </div>
        <div className="hidden items-center gap-3 text-xs font-bold text-slate-500 dark:text-slate-400 sm:flex">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-slate-300 dark:bg-slate-500" />{t("challenge.chart.trajectory")}</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-violet-500" />{t("challenge.chart.real")}</span>
        </div>
      </div>

      <svg viewBox="0 0 100 100" className="h-36 w-full overflow-visible" preserveAspectRatio="none">
        <polyline points={chartData.expectedPath} fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" className="text-slate-300 dark:text-slate-600" />
        <polyline points={chartData.realPath} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-violet-600 dark:text-violet-300" />
      </svg>
    </div>
  );
});

function ModalShell({ children, size = "md" }) {
  const sizeClass = size === "lg" ? "max-w-5xl" : "max-w-2xl";
  return (
    <div className="challenge-modal-backdrop fixed inset-y-0 right-0 z-[5000] overflow-y-auto px-3 py-4 sm:px-5 sm:py-6">
      <div className={cn("mx-auto flex min-h-full w-full items-center justify-center", sizeClass)}>{children}</div>
    </div>
  );
}

function ObjectiveModeButton({ active, icon: Icon, title, description, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-2xl border p-4 text-left transition",
        active
          ? "border-violet-500 bg-violet-50  shadow-violet-500/10 dark:border-violet-400 dark:bg-violet-400/10"
          : "border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/50 dark:border-white/10 dark:bg-slate-950/60 dark:hover:border-violet-400/40 dark:hover:bg-violet-400/5",
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", active ? "bg-violet-100 text-violet-700 dark:bg-violet-400/15 dark:text-violet-200" : "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300")}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-black text-slate-950 dark:text-white">{title}</p>
          <p className="mt-1 text-xs font-medium leading-5 text-slate-600 dark:text-slate-300">{description}</p>
        </div>
      </div>
    </button>
  );
}

function MetricBox({ label, value, positive, violet }) {
  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{label}</p>
      <p className={cn("mt-1 text-base font-black tracking-[-0.02em]", positive ? "text-emerald-600 dark:text-emerald-300" : violet ? "text-violet-600 dark:text-violet-300" : "text-slate-950 dark:text-white")}>
        {value}
      </p>
    </div>
  );
}

function CreateChallengeModal({ open, onClose, onCreate, agent, creating }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [objectiveMode, setObjectiveMode] = useState("percent");
  const [targetPercent, setTargetPercent] = useState(25);
  const [targetAmount, setTargetAmount] = useState("");
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!open) return;
    const capital = Number(agent.equity || agent.balance || 0);
    if (capital > 0) {
      setTargetAmount(String(Math.round(capital * 1.25 * 100) / 100));
    }
  }, [open, agent.equity, agent.balance]);

  if (!open) return null;

  const agentReady = agent.connected && Number(agent.balance || 0) > 0;
  const safeCapital = Math.max(Number(agent.equity || agent.balance || 0), 1);
  const safeDays = clamp(days, 1, 365);
  const safePercent = Math.max(Number(targetPercent || 0), 0.01);
  const computedTargetAmount = objectiveMode === "percent" ? safeCapital * (1 + safePercent / 100) : Math.max(Number(targetAmount || 0), safeCapital + 0.01);
  const computedTargetPercent = ((computedTargetAmount - safeCapital) / safeCapital) * 100;
  const targetProfit = computedTargetAmount - safeCapital;
  const dailyTarget = targetProfit / safeDays;
  const canSubmit = agentReady && computedTargetAmount > safeCapital && safeDays >= 1 && !creating;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const now = new Date().toISOString();
    const finalName = name.trim() || (objectiveMode === "percent" ? t("challenge.create.defaultNamePercent", { percent: formatPercent(computedTargetPercent) }) : t("challenge.create.defaultNameAmount", { amount: formatMoney(computedTargetAmount) }));

    onCreate({
      name: finalName,
      objectiveMode,
      targetPercent: Number(computedTargetPercent.toFixed(4)),
      targetAmount: Number(computedTargetAmount.toFixed(2)),
      targetProfit: Number(targetProfit.toFixed(2)),
      startCapital: Number(safeCapital.toFixed(2)),
      currentCapital: Number(safeCapital.toFixed(2)),
      targetCapital: Number(computedTargetAmount.toFixed(2)),
      days: safeDays,
      status: "active",
      source: "agent",
      startDate: now,
      endDate: addDays(now, safeDays),
      entries: [{ day: 0, capital: Number(safeCapital.toFixed(2)), date: now, source: "agent" }],
      createdAt: now,
      updatedAt: now,
    });
  };

  return (
    <ModalShell>
      <div className="challenge-modal-card challenge-glass w-full overflow-hidden rounded-2xl">
        <div className="border-b border-slate-200/80 bg-white/55 p-4 backdrop-blur dark:border-white/10 dark:bg-white/[0.03]">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-violet-100 text-violet-700 dark:bg-violet-400/10 dark:text-violet-200">
                <Sparkles className="h-[18px] w-[18px]" />
              </div>
              <div>
                <h2 className="text-lg font-black tracking-[-0.02em] text-slate-950 dark:text-white">{t("challenge.create.title")}</h2>
                <p className="mt-1 text-[13px] font-medium text-slate-600 dark:text-slate-300">{t("challenge.create.subtitle")}</p>
              </div>
            </div>
            <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white" aria-label={t("challenge.common.close")}>
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>

        <div className="challenge-scrollbar max-h-[calc(100vh-150px)] overflow-y-auto p-4">
          <div>
            <label className="mb-2 block text-[13px] font-black text-slate-950 dark:text-white">{t("challenge.create.nameLabel")}</label>
            <input
              type="text"
              value={name}
              placeholder={t("challenge.create.namePlaceholder")}
              onChange={(event) => setName(event.target.value)}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-950 outline-none transition focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 dark:border-white/10 dark:bg-slate-900 dark:text-white"
            />
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <ObjectiveModeButton active={objectiveMode === "percent"} icon={Percent} title={t("challenge.create.modePercentTitle")} description={t("challenge.create.modePercentDesc")} onClick={() => setObjectiveMode("percent")} />
            <ObjectiveModeButton active={objectiveMode === "amount"} icon={Banknote} title={t("challenge.create.modeAmountTitle")} description={t("challenge.create.modeAmountDesc")} onClick={() => setObjectiveMode("amount")} />
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-[13px] font-black text-slate-950 dark:text-white">{t("challenge.create.detectedCapital")}</label>
              <div className="relative">
                <input type="number" value={Number(safeCapital.toFixed(2))} readOnly className="h-11 w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-100 px-3 pr-10 text-sm font-bold text-slate-950 outline-none dark:border-white/10 dark:bg-white/10 dark:text-white" />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-black text-slate-500 dark:text-slate-400">$</span>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-[13px] font-black text-slate-950 dark:text-white">{t("challenge.create.daysLabel")}</label>
              <input type="number" min="1" max="365" value={days} onChange={(event) => setDays(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-950 outline-none transition focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 dark:border-white/10 dark:bg-slate-900 dark:text-white" />
              <p className="mt-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">{t("challenge.create.daysHint")}</p>
            </div>
          </div>

          {objectiveMode === "percent" ? (
            <div className="mt-4">
              <label className="mb-2 block text-[13px] font-black text-slate-950 dark:text-white">{t("challenge.create.percentLabel")}</label>
              <div className="relative">
                <input type="number" min="0.01" step="0.01" value={targetPercent} onChange={(event) => setTargetPercent(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 pr-10 text-sm font-bold text-slate-950 outline-none transition focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 dark:border-white/10 dark:bg-slate-900 dark:text-white" />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-black text-slate-500 dark:text-slate-400">%</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {OBJECTIVE_PRESETS.map((preset) => (
                  <button key={preset.value} type="button" onClick={() => setTargetPercent(preset.value)} className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-xs font-black text-slate-700 transition hover:border-violet-300 hover:text-violet-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-200">
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <label className="mb-2 block text-[13px] font-black text-slate-950 dark:text-white">{t("challenge.create.amountLabel")}</label>
              <div className="relative">
                <input type="number" min={safeCapital + 0.01} step="0.01" value={targetAmount} onChange={(event) => setTargetAmount(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 pr-10 text-sm font-bold text-slate-950 outline-none transition focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 dark:border-white/10 dark:bg-slate-900 dark:text-white" />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-black text-slate-500 dark:text-slate-400">$</span>
              </div>
              <p className="mt-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">{t("challenge.create.amountHint")}</p>
            </div>
          )}

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900/70">
            <div className="grid gap-4 sm:grid-cols-3">
              <MetricBox label={t("challenge.create.metricObjective")} value={`${formatMoney(computedTargetAmount)}$`} />
              <MetricBox label={t("challenge.create.metricProfit")} value={`+${formatMoney(targetProfit)}$`} positive />
              <MetricBox label={t("challenge.create.metricDaily")} value={`${formatMoney(dailyTarget)}$`} violet />
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-violet-50 p-4 text-[13px] font-medium leading-6 text-violet-900 dark:bg-violet-400/10 dark:text-violet-100">
            {t("challenge.create.info")}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-200/80 bg-white/55 p-4 backdrop-blur dark:border-white/10 dark:bg-white/[0.03] sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={creating} className="h-9 rounded-xl px-4 text-[13px] font-black text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:text-slate-200 dark:hover:bg-white/10">
            {t("challenge.common.cancel")}
          </button>
          <PrimaryButton onClick={handleSubmit} disabled={!canSubmit}>
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            {creating ? t("challenge.create.creating") : t("challenge.create.submit")}
          </PrimaryButton>
        </div>
      </div>
    </ModalShell>
  );
}

function ChallengeDetailsLoading({ challenge, onClose }) {
  const { t } = useTranslation();
  return (
    <ModalShell size="lg">
      <div className="challenge-modal-card challenge-glass w-full overflow-hidden rounded-2xl">
        <div className="border-b border-slate-200/80 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.04]">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-violet-700 dark:border-violet-400/20 dark:bg-violet-400/10 dark:text-violet-200">{t("challenge.details.badge")}</div>
              <h2 className="truncate text-lg font-black tracking-[-0.02em] text-slate-950 dark:text-white">{challenge?.name || t("challenge.details.fallbackName")}</h2>
            </div>
            <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white" aria-label={t("challenge.common.close")}>
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
        <div className="grid min-h-[280px] place-items-center p-6 text-center">
          <div>
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-400/10 dark:text-violet-200"><Loader2 className="h-5 w-5 animate-spin" /></div>
            <p className="mt-4 text-sm font-black text-slate-950 dark:text-white">{t("challenge.details.opening")}</p>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

function ChallengeDetailsModal({ challenge, onClose, agent }) {
  const { t } = useTranslation();
  const [contentReady, setContentReady] = useState(false);
  const [showFullTrajectory, setShowFullTrajectory] = useState(false);

  useEffect(() => {
    if (!challenge) return undefined;
    setContentReady(false);
    setShowFullTrajectory(false);
    const timeoutId = window.setTimeout(() => setContentReady(true), 60);
    return () => window.clearTimeout(timeoutId);
  }, [challenge?.id]);

  const trajectory = useMemo(() => (challenge ? buildTrajectory(challenge) : []), [challenge]);
  const visibleTrajectory = useMemo(() => {
    if (!challenge || showFullTrajectory || trajectory.length <= 140) return trajectory;
    const elapsed = getElapsedDays(challenge);
    const head = trajectory.slice(0, 18);
    const middle = trajectory.slice(Math.max(18, elapsed - 28), Math.min(trajectory.length, elapsed + 45));
    const tail = trajectory.slice(Math.max(Math.min(trajectory.length, elapsed + 45), trajectory.length - 18));
    const byDay = new Map([...head, ...middle, ...tail].map((item) => [item.day, item]));
    return Array.from(byDay.values()).sort((a, b) => a.day - b.day);
  }, [challenge, showFullTrajectory, trajectory]);

  if (!challenge) return null;
  if (!contentReady) return <ChallengeDetailsLoading challenge={challenge} onClose={onClose} />;

  const progress = getProgress(challenge);
  const elapsed = getElapsedDays(challenge);
  const expectedToday = getExpectedCapitalAtDay(challenge, elapsed);
  const current = Number(challenge.currentCapital || challenge.startCapital || 0);
  const agentReady = agent.connected && Number(agent.equity || agent.balance || 0) > 0;
  const capitalToday = Number(agent.equity || agent.balance || current || 0);
  const gap = current - expectedToday;
  const profit = current - Number(challenge.startCapital || 0);
  const profitPercent = getProfitPercent(challenge);
  const hiddenRows = Math.max(0, trajectory.length - visibleTrajectory.length);

  return (
    <ModalShell size="lg">
      <div className="challenge-modal-card challenge-glass w-full overflow-hidden rounded-2xl">
        <div className="border-b border-slate-200/80 bg-white/55 p-4 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-black tracking-[-0.02em] text-slate-950 dark:text-white sm:text-xl">{challenge.name}</h2>
              <p className="mt-1 text-[13px] font-medium text-slate-600 dark:text-slate-300 sm:text-[14px]">
                {challenge.objectiveMode === "amount" ? t("challenge.details.targetCapital", { amount: formatMoney(challenge.targetCapital) }) : t("challenge.details.targetObjective", { percent: formatPercent(challenge.targetPercent) })} · {t("challenge.details.daysCount", { days: challenge.days })}
              </p>
            </div>
            <button type="button" onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white" aria-label={t("challenge.common.close")}>
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>

        <div className="challenge-scrollbar max-h-[calc(100dvh-150px)] overflow-y-auto p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <MiniBox label={t("challenge.common.start")} value={`${formatMoney(challenge.startCapital)}$`} />
            <MiniBox label={t("challenge.common.objective")} value={`${formatMoney(challenge.targetCapital)}$`} active />
            <MiniBox label={t("challenge.common.current")} value={`${formatMoney(current)}$`} />
            <MiniBox label={t("challenge.details.profit")} value={`${profit >= 0 ? "+" : ""}${formatMoney(profit)}$`} />
            <MiniBox label={t("challenge.common.progress")} value={`${Math.round(progress)}%`} />
          </div>

          <div className={cn("mt-4 rounded-2xl p-3.5 text-[13px] font-black sm:text-[14px]", gap >= 0 ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200" : "bg-rose-50 text-rose-700 dark:bg-rose-400/10 dark:text-rose-200")}>
            {gap >= 0 ? t("challenge.details.ahead", { amount: formatMoney(gap), percent: formatPercent(profitPercent) }) : t("challenge.details.behind", { amount: formatMoney(Math.abs(gap)), percent: formatPercent(profitPercent) })}
          </div>

          <div className="mt-4"><MiniChart trajectory={trajectory} /></div>

          <div className="challenge-contained mt-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950/60">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 text-slate-950 dark:text-white">
                  <Database className="h-[18px] w-[18px] text-violet-500" />
                  <h3 className="text-base font-black">{t("challenge.details.syncTitle")}</h3>
                </div>
                <p className="mt-1 text-[13px] font-medium leading-5 text-slate-600 dark:text-slate-300">{t("challenge.details.syncDesc")}</p>
              </div>
              <span className={cn("inline-flex h-10 items-center justify-center gap-2 rounded-[14px] px-4 text-sm font-extrabold", agentReady ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200" : "bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-200")}>
                {agentReady ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
                {agentReady ? t("challenge.details.agentSynced") : t("challenge.agent.pending")}
              </span>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <MiniBox label={t("challenge.details.capitalReceived")} value={`${formatMoney(capitalToday)}$`} />
              <MiniBox label={t("challenge.details.equity")} value={`${formatMoney(agent.equity || capitalToday)}$`} />
              <MiniBox label={t("challenge.details.source")} value={agent.accountLogin ? t("challenge.details.sourceMt5", { account: agent.accountLogin }) : t("challenge.common.agent")} />
            </div>
          </div>

          <div className="challenge-contained mt-4 overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/[0.04]">
              <h3 className="text-sm font-black text-slate-950 dark:text-white">{t("challenge.details.trajectory")}</h3>
              {hiddenRows > 0 ? (
                <button type="button" onClick={() => setShowFullTrajectory(true)} className="rounded-xl bg-violet-50 px-3 py-1.5 text-[11px] font-black text-violet-700 transition hover:bg-violet-100 dark:bg-violet-400/10 dark:text-violet-200 dark:hover:bg-violet-400/20">{t("challenge.details.showAll")}</button>
              ) : null}
            </div>

            <div className="challenge-scrollbar max-h-[280px] overflow-auto">
              <table className="w-full min-w-[680px] text-left text-[13px]">
                <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-[0.12em] text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-3">{t("challenge.details.colDay")}</th>
                    <th className="px-4 py-3">{t("challenge.details.colDate")}</th>
                    <th className="px-4 py-3">{t("challenge.details.colExpected")}</th>
                    <th className="px-4 py-3">{t("challenge.details.colReal")}</th>
                    <th className="px-4 py-3">{t("challenge.details.colGap")}</th>
                    <th className="px-4 py-3">{t("challenge.details.colStatus")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/10">
                  {visibleTrajectory.map((row) => {
                    const hasReal = row.real !== null;
                    const rowGap = hasReal ? row.gap : null;
                    return (
                      <tr key={row.day} className="text-slate-700 dark:text-slate-200">
                        <td className="px-4 py-3 font-black">{t("challenge.details.dayShort", { day: row.day })}</td>
                        <td className="px-4 py-3 font-medium">{formatDate(row.date)}</td>
                        <td className="px-4 py-3 font-bold">{formatMoney(row.expected)}$</td>
                        <td className="px-4 py-3 font-bold">{hasReal ? `${formatMoney(row.real)}$` : "—"}</td>
                        <td className={cn("px-4 py-3 font-black", rowGap === null ? "text-slate-400" : rowGap >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300")}>
                          {rowGap === null ? "—" : `${rowGap >= 0 ? "+" : ""}${formatMoney(rowGap)}$`}
                        </td>
                        <td className="px-4 py-3">{!hasReal ? <span className="text-slate-400">—</span> : rowGap >= 0 ? <span className="font-black text-emerald-600 dark:text-emerald-300">{t("challenge.details.ok")}</span> : <span className="font-black text-rose-600 dark:text-rose-300">{t("challenge.details.late")}</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 flex justify-end"><DarkButton onClick={onClose}>{t("challenge.common.close")}</DarkButton></div>
        </div>
      </div>
    </ModalShell>
  );
}

function ChallengeSyncNotice({ state, onRetry }) {
  const { t } = useTranslation();
  // Rien à afficher quand tout fonctionne : les utilisateurs n'ont pas besoin
  // de voir un message technique sur la synchronisation ou l'absence de cache local.
  if (!state || state.loading || !state.error) return null;

  const isAuth = state.error === "auth_required" || state.status === 401 || state.status === 403;
  const text = isAuth
    ? t("challenge.sync.authError")
    : state.error === "challenge_endpoint_missing"
      ? t("challenge.sync.unavailable")
      : t("challenge.sync.loadError");

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-semibold leading-5 text-amber-800 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between">
      <span className="inline-flex items-start gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {text}</span>
      <button type="button" onClick={onRetry} className="inline-flex h-9 items-center justify-center gap-2 rounded-xl bg-white/75 px-3 text-xs font-black text-amber-800 transition hover:bg-white dark:bg-white/10 dark:text-amber-100 dark:hover:bg-white/15">
        <RefreshCw className="h-3.5 w-3.5" /> {t("challenge.sync.retry")}
      </button>
    </div>
  );
}

export default function ChallengePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const agent = useAgentAccount();

  const [challenges, setChallenges] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [creatingChallenge, setCreatingChallenge] = useState(false);
  const [selectedChallengeId, setSelectedChallengeId] = useState(null);
  const [syncState, setSyncState] = useState({ loading: true, error: null, status: null });
  const [refreshIndex, setRefreshIndex] = useState(0);
  const autoSyncSignatureRef = useRef("");
  const mountedRef = useRef(true);

  const agentReady = agent.connected && Number(agent.balance || 0) > 0;
  const stats = useMemo(() => getChallengeStats(challenges), [challenges]);
  const selectedChallenge = useMemo(() => challenges.find((item) => String(item.id) === String(selectedChallengeId)) || null, [challenges, selectedChallengeId]);

  const refreshChallenges = useCallback(() => {
    setRefreshIndex((value) => value + 1);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    removeLegacyChallengeCache();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setSyncState({ loading: true, error: null, status: null });

    fetchRemoteChallenges()
      .then((remoteChallenges) => {
        if (!active) return;
        setChallenges(remoteChallenges);
        setSyncState({ loading: false, error: null, status: 200 });
      })
      .catch((error) => {
        if (!active) return;
        setChallenges([]);
        setSelectedChallengeId(null);
        setCreateOpen(false);
        setSyncState({
          loading: false,
          error: error?.status === 404 ? "challenge_endpoint_missing" : error?.message || "challenge_sync_failed",
          status: error?.status || null,
        });
      });

    return () => {
      active = false;
    };
  }, [refreshIndex]);

  useEffect(() => {
    const onAuthChanged = () => {
      setChallenges([]);
      setSelectedChallengeId(null);
      setCreateOpen(false);
      autoSyncSignatureRef.current = "";
      refreshChallenges();
    };

    window.addEventListener("storage", onAuthChanged);
    window.addEventListener("focus", onAuthChanged);
    return () => {
      window.removeEventListener("storage", onAuthChanged);
      window.removeEventListener("focus", onAuthChanged);
    };
  }, [refreshChallenges]);

  useEffect(() => {
    if (!agentReady || syncState.loading || syncState.error || !challenges.length) return;

    const syncedCapital = Number(agent.equity || agent.balance || 0);
    if (syncedCapital <= 0) return;

    const todayKey = new Date().toISOString().slice(0, 10);
    const activeIds = challenges
      .filter((challenge) => ["active", "late"].includes(getChallengeStatus(challenge)))
      .map((challenge) => `${challenge.id}:${getElapsedDays(challenge)}:${challenge.currentCapital}`)
      .join("|");
    const signature = `${agent.accountLogin || "account"}|${todayKey}|${syncedCapital}|${activeIds}`;

    if (signature === autoSyncSignatureRef.current) return;
    autoSyncSignatureRef.current = signature;

    const updates = [];
    const now = new Date().toISOString();

    setChallenges((prev) => {
      let changed = false;
      const next = prev.map((challenge) => {
        const status = getChallengeStatus(challenge);
        if (status !== "active" && status !== "late") return challenge;

        const day = getElapsedDays(challenge);
        const existing = (challenge.entries || []).find((item) => Number(item.day) === Number(day));

        if (existing && Number(existing.capital) === syncedCapital && Number(challenge.currentCapital || 0) === syncedCapital) {
          return challenge;
        }

        changed = true;
        const entry = { day, capital: Number(syncedCapital.toFixed(2)), date: now, source: "agent" };
        const entries = [...(challenge.entries || []).filter((item) => Number(item.day) !== Number(day)), entry].sort((a, b) => Number(a.day) - Number(b.day));
        const updated = { ...challenge, entries, currentCapital: Number(syncedCapital.toFixed(2)), updatedAt: now };
        updates.push(updated);
        return updated;
      });
      return changed ? next : prev;
    });

    if (!updates.length) return;

    Promise.allSettled(updates.map((challenge) => updateRemoteChallenge(challenge))).then((results) => {
      if (!mountedRef.current) return;
      const fulfilled = results
        .map((result) => (result.status === "fulfilled" ? result.value : null))
        .filter(Boolean);

      if (!fulfilled.length) return;
      setChallenges((prev) => prev.map((challenge) => fulfilled.find((item) => String(item.id) === String(challenge.id)) || challenge));
    });
  }, [agent.accountLogin, agent.balance, agent.equity, agentReady, challenges, syncState.error, syncState.loading]);

  const openAgentPage = useCallback(() => {
    navigate("/agent");
  }, [navigate]);

  const openCreateModal = useCallback(() => {
    if (!agentReady) {
      navigate("/agent");
      return;
    }
    setCreateOpen(true);
  }, [agentReady, navigate]);

  const openChallengeDetails = useCallback((challenge) => {
    const id = typeof challenge === "string" ? challenge : challenge?.id;
    if (!id) return;
    setSelectedChallengeId(id);
  }, []);

  const handleCreateChallenge = useCallback(async (challenge) => {
    if (creatingChallenge) return;
    setCreatingChallenge(true);
    setSyncState((current) => ({ ...current, error: null }));

    try {
      const remoteChallenge = await createRemoteChallenge(challenge);
      if (!mountedRef.current) return;
      setChallenges((prev) => [remoteChallenge, ...prev.filter((item) => String(item.id) !== String(remoteChallenge.id))]);
      setCreateOpen(false);
      setSelectedChallengeId(remoteChallenge.id);
      setSyncState({ loading: false, error: null, status: 200 });
    } catch (error) {
      if (!mountedRef.current) return;
      setSyncState({
        loading: false,
        error: error?.status === 404 ? "challenge_endpoint_missing" : error?.message || "challenge_create_failed",
        status: error?.status || null,
      });
    } finally {
      if (mountedRef.current) setCreatingChallenge(false);
    }
  }, [creatingChallenge]);

  const isRefreshing = syncState.loading;

  return (
    <>
      <ChallengePageStyles />

      <main className="challenge-page min-h-screen bg-slate-50 px-3 py-3 text-slate-950 dark:bg-slate-950 dark:text-white sm:px-4 lg:px-5">
        <div className="mx-auto w-full max-w-[1320px] space-y-4">
          <ChallengeHeader agent={agent} onCreate={openCreateModal} onOpenAgent={openAgentPage} onRefresh={refreshChallenges} refreshing={isRefreshing} />

          <AgentSummaryCard agent={agent} />

          <ChallengeSyncNotice state={syncState} onRetry={refreshChallenges} />

          <OverviewStats stats={stats} />

          <PerformanceSummary stats={stats} />

          <section className="pt-1">
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl font-black tracking-[-0.04em] text-slate-950 dark:text-white">{t("challenge.list.title")}</h2>
                <p className="mt-1 text-[13px] font-medium leading-5 text-slate-600 dark:text-slate-300">
                  {t("challenge.list.subtitle")}
                </p>
              </div>

              {agentReady ? (
                <DarkButton onClick={openCreateModal} disabled={Boolean(syncState.error && syncState.status === 404)}>
                  <Plus className="h-4 w-4" />
                  {t("challenge.header.create")}
                </DarkButton>
              ) : null}
            </div>

            {syncState.loading ? (
              <div className="challenge-glass grid min-h-[220px] place-items-center rounded-2xl p-7 text-center sm:p-8">
                <div>
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-400/10 dark:text-violet-200">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                  <h3 className="mt-4 text-lg font-black tracking-[-0.02em] text-slate-950 dark:text-white">{t("challenge.list.loadingTitle")}</h3>
                  <p className="mx-auto mt-2 max-w-xl text-[13px] font-medium leading-6 text-slate-600 dark:text-slate-300">{t("challenge.list.loadingDesc")}</p>
                </div>
              </div>
            ) : challenges.length === 0 ? (
              <div className="challenge-glass rounded-2xl border-dashed p-7 text-center sm:p-8">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-400/10 dark:text-violet-200">
                  <Target className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-black tracking-[-0.02em] text-slate-950 dark:text-white">{t("challenge.list.emptyTitle")}</h3>
                <p className="mx-auto mt-2 max-w-xl text-[13px] font-medium leading-6 text-slate-600 dark:text-slate-300">
                  {syncState.error
                    ? t("challenge.list.emptyError")
                    : agentReady
                      ? t("challenge.list.emptyReady")
                      : t("challenge.list.emptyNotReady")}
                </p>
                {syncState.error ? (
                  <DarkButton onClick={refreshChallenges} className="mt-5">
                    <RefreshCw className="h-4 w-4" />
                    {t("challenge.sync.retry")}
                  </DarkButton>
                ) : agentReady ? (
                  <PrimaryButton onClick={openCreateModal} className="mt-5">
                    <Rocket className="h-4 w-4" />
                    {t("challenge.list.createFirst")}
                  </PrimaryButton>
                ) : (
                  <DarkButton onClick={openAgentPage} className="mt-5">
                    <Wifi className="h-4 w-4" />
                    {t("challenge.common.openAgent")}
                  </DarkButton>
                )}
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {challenges.map((challenge) => (
                  <ChallengeCard key={challenge.id} challenge={challenge} onOpen={openChallengeDetails} />
                ))}
              </div>
            )}
          </section>
        </div>

        <CreateChallengeModal open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreateChallenge} agent={agent} creating={creatingChallenge} />
        <ChallengeDetailsModal challenge={selectedChallenge} onClose={() => setSelectedChallengeId(null)} agent={agent} />
      </main>
    </>
  );
}
